import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { CatalogRatioUpdateInput } from 'gql/graphql';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { updateCatalogRatioGql } from '../catalogRatiosQueries';

export const updateCatalogRatioItemRequestId = 'updateCatalogRatio';

export const useUpdateRatio = ({
    closeModalHandler,
    refreshData,
}: {
    closeModalHandler: () => void;
    refreshData: () => void;
}) => {
    const dispatch = useDispatch();

    const [resetUpdateData] = useResetStoreData({ requestId: updateCatalogRatioItemRequestId });
    const [validationMessages, setValidationMessages] = useState<Record<string, string | null>>({});

    const tempSuccessHandler = useCallback(() => {
        closeModalHandler();
        refreshData();
        resetUpdateData(undefined);
    }, [closeModalHandler, resetUpdateData, refreshData]);

    const [updateRatioData, updateRatioStatus, , updateRatioRequest] = useRequest({
        requestId: updateCatalogRatioItemRequestId,
        query: updateCatalogRatioGql,
        withEffect: false,
        dependenciesUpdater: tempSuccessHandler,
    });

    useEffect(() => {
        if (updateRatioData?.updateCatalogRatio?.validation?.length) {
            const validationState = updateRatioData?.updateCatalogRatio?.validation.reduce(
                (acc, v) => ({
                    ...acc,
                    [v.property ?? 'unknown']: v.message,
                }),
                {},
            );

            setValidationMessages(validationState);
        } else {
            setValidationMessages({});
        }
    }, [updateRatioData?.updateCatalogRatio?.validation]);

    const updateRatioHandler = useCallback(
        (input: CatalogRatioUpdateInput) => {
            dispatch(updateRatioRequest({ input }));
        },
        [updateRatioRequest, dispatch],
    );

    return useMemo(
        () => ({
            updateRatioStatus,
            updateRatioHandler,
            resetUpdateData,
            validationMessages,
        }),
        [updateRatioHandler, updateRatioStatus, resetUpdateData, validationMessages],
    );
};
